/**
 * Cliente activo (módulo Medios): selección global persistida en localStorage.
 * `null` = "Todos los clientes" (default). Espejo de use-active-project, pero
 * sin auto-select del primero: el estado sin filtro es válido.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { AgencyClientOut } from "@/lib/api";

const STORAGE_KEY = "tm_active_client_id";

type Ctx = {
  activeClientId: number | null; // null = todos los clientes
  setActiveClientId: (id: number | null) => void;
  resolve: (clients: AgencyClientOut[] | undefined) => AgencyClientOut | null;
};

const ActiveClientCtx = createContext<Ctx | null>(null);

export function ActiveClientProvider({ children }: { children: ReactNode }) {
  const [activeClientId, setActiveClientIdState] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  });

  function setActiveClientId(id: number | null) {
    setActiveClientIdState(id);
    if (typeof window !== "undefined") {
      if (id == null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, String(id));
    }
  }

  const value = useMemo<Ctx>(
    () => ({
      activeClientId,
      setActiveClientId,
      resolve(clients) {
        if (activeClientId == null || !clients) return null;
        return clients.find((c) => c.id === activeClientId) ?? null;
      },
    }),
    [activeClientId],
  );

  return <ActiveClientCtx.Provider value={value}>{children}</ActiveClientCtx.Provider>;
}

export function useActiveClient() {
  const ctx = useContext(ActiveClientCtx);
  if (!ctx) throw new Error("useActiveClient must be used inside <ActiveClientProvider>");
  return ctx;
}
